import { useCallback, useRef } from "react";

import { useSse, type SseMessage } from "@platform/hooks/useSse";

// ── Types ─────────────────────────────────────────────────────────────────────

interface ResourceStatusChangedPayload {
  resourceId: number;
  resourceAlias: string;
  projectAlias: string;
  status: string;
}

export interface ResourceStatusTransition {
  resourceAlias: string;
  previousStatus: string | null;
  newStatus: string;
}

interface UseResourceRealtimeReturn {
  /** Whether the SSE connection is currently active. */
  connected: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * useResourceRealtime — subscribes to resource status changes for a project
 * via SSE (Server-Sent Events).
 *
 * When a `resource.status_changed` event is received:
 * - `onStatusChange` is invoked so the caller can refetch resources.
 * - `onStatusTransition` is invoked when the status actually changed, so the
 *   caller can show toast notifications for meaningful transitions.
 *
 * @param projectAlias  Project alias to subscribe to (undefined → no-op)
 * @param onStatusChange  Called when a resource status change event arrives
 * @param onStatusTransition  Optional callback for status transitions (toasts)
 */
export function useResourceRealtime(
  projectAlias: string | undefined,
  onStatusChange: () => void,
  onStatusTransition?: (transition: ResourceStatusTransition) => void,
): UseResourceRealtimeReturn {
  // Track the last known status per resource alias so we can detect real
  // transitions (and avoid firing toast callbacks for no-op events).
  const statusMapRef = useRef<Map<string, string>>(new Map());
  const callbackRef = useRef(onStatusChange);
  callbackRef.current = onStatusChange;
  const transitionRef = useRef(onStatusTransition);
  transitionRef.current = onStatusTransition;

  const ssePath = projectAlias ? `sse/project/${projectAlias}` : null;

  const handleMessage = useCallback((msg: SseMessage) => {
    if (msg.type !== "resource.status_changed") return;

    const data = msg.data as ResourceStatusChangedPayload;
    const previousStatus = statusMapRef.current.get(data.resourceAlias) ?? null;
    statusMapRef.current.set(data.resourceAlias, data.status);

    // Always notify so callers can refetch.
    callbackRef.current();

    // Only fire the transition callback when the status actually changed.
    if (previousStatus !== data.status) {
      transitionRef.current?.({
        resourceAlias: data.resourceAlias,
        previousStatus,
        newStatus: data.status,
      });
    }
  }, []);

  const { connected } = useSse(ssePath, handleMessage);

  return { connected };
}
